import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  DatePicker,
  Input,
  Upload,
  Row,
  Col,
  message,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import styled from "styled-components";
import { ButtonFilled, ButtonOutlined } from "reusableComponent/UIButtons";
import { CALL_API, CALL_FEATCH_API } from "common/API";
import { useSelector } from "react-redux";
import { getClientid, getUserInfo, getUserList } from "redux/selectors";
import { STATUS_CODE } from "common/Constants";
import SuccessDeclinePopup from "reusableComponent/Popup/SuccessDeclinePopup";


const ModalWrap = styled(Modal)`
  .ant-modal-header {
    border-bottom: none;
  }
  .ant-modal-body {
    padding: 0px 24px 24px 24px;
  }
  .custom-upload .ant-upload {
    width: 100%;
  }

  .upload-box {
    width: 100%;
    height: 42px;
    border-radius: 12px;
    background: #cfd6ff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 500;
    cursor: pointer;
    border: 1px solid #cfd6ff;
  }

  .upload-box:hover {
    background: #bcc6ff;
  }
`;

const { TextArea } = Input;

const ApplyWorkFromHome = ({
  open,
  onCancel,
  data = {},
  getData = () => { },
}) => {
  const [form] = Form.useForm();
  const userInfo = useSelector(getUserInfo);
  const rawUserList = useSelector(getUserList);
  const userList = Array.isArray(rawUserList) ? rawUserList : [];
  const clientId = useSelector(getClientid);

  const currentUserRecord = userList.find((u) => u?.userid === userInfo?.userid);

  const officeSupervisorRecord = userList.find(
    (u) => u?.employeeid === currentUserRecord?.OfficeSupervisorId
  );
  const [loading, setLoading] = useState(false);
  const [initialValues] = useState({});
  const [popupOpen, setPopupOpen] = useState(false);
  const [holidayRanges, setHolidayRanges] = useState([]);

  useEffect(() => {
    if (!clientId) return;
    const fetchHolidays = async () => {
      const now = dayjs();
      const startDate = now.startOf("month").format("YYYY-MM-DD");
      const endDate = now.endOf("month").format("YYYY-MM-DD");
      try {
        const res = await CALL_FEATCH_API(
          `holiday-list/${clientId}`,
          "post",
          { start_date: startDate, end_date: endDate }
        );
        const userLocationId = String(currentUserRecord?.baselocationid || "");
        const allHolidays = res?.response || [];
        const filtered = userLocationId
          ? allHolidays.filter((h) =>
            h?.location_id?.map(String).includes(userLocationId)
          )
          : [];
        const holidays = filtered.length ? filtered : allHolidays;
        setHolidayRanges(
          holidays.map((h) => ({
            start: dayjs(h.start_date),
            end: dayjs(h.end_date),
            name: h.holiday_name,
          }))
        );
      } catch (err) {
        console.error("[HolidayList] error:", err);
      }
    };
    fetchHolidays();
  }, [clientId]);

  useEffect(() => {
    if (data && Object.keys(data).length) {
      form.setFieldsValue({
        startdate: data?.startDate ? dayjs(data.startDate) : null,
        enddate: data?.endDate ? dayjs(data.endDate) : null,
        noofdays: data?.noOfDays || "",
        reason: data?.reason || "",
        attachment:
          data?.attachedDocuments?.length > 0
            ? data.attachedDocuments.map((doc, idx) => ({
              uid: String(idx),
              name: doc?.fileName || `attachment_${idx + 1}`,
              status: "done",
              url: doc?.fileUrl || "",
              objectKey: doc?.objectKey || "",
            }))
            : [],
      });
    }
  }, [data]);

  const getHolidayName = (date) => {
    const d = dayjs(date).startOf("day").valueOf();
    const matched = holidayRanges.find(
      (h) =>
        d >= dayjs(h.start).startOf("day").valueOf() &&
        d <= dayjs(h.end).startOf("day").valueOf()
    );
    return matched ? matched.name : null;
  };

  const isDisabledDate = (date) => {
    if (!date) return false;
    if (date.day() === 0 || date.day() === 6) return true;
    return !!getHolidayName(date);
  };

  const handleDateSelect = (date, field) => {
    if (!date) return;
    const holidayName = getHolidayName(date);
    if (holidayName) {
      message.warning(`You cannot apply WFH on "${holidayName}"`);
      form.setFieldsValue({ [field]: null });
      return;
    }
    if (field === "startdate") {
      const endDate = form.getFieldValue("enddate");
      if (endDate && date.isAfter(endDate)) {
        message.warning("Start date cannot be after end date");
        return;
      }
      handleStartDateChange(date);
    } else {
      const startDate = form.getFieldValue("startdate");
      if (startDate && date.isBefore(startDate)) {
        message.warning("End date cannot be before start date");
        return;
      }
      calculateDays(startDate, date);
    }
  };

  const handleDocumentSubmit = async (file) => {
    try {
      const res = await CALL_API(`wfh/insertwfhrequest?file`, "POST", {
        filenames: [file.name],
      });
      if (!res?.uploadFileData?.length) {
        throw new Error("Failed to get presigned URL");
      }
      const { presignedUrl, fileKey } = res.uploadFileData[0];
      if (!presignedUrl) throw new Error("Presigned URL missing");

      const putRes = await fetch(presignedUrl, {
        method: "PUT",
        headers: {
          "Content-Type": file.type || "application/octet-stream",
        },
        body: file,
      });

      if (!putRes.ok) throw new Error("S3 upload failed");

      return { objectKey: fileKey, fileName: file.name };
    } catch (err) {
      console.error(err);
      message.error("File upload failed");
      return null;
    }
  };

  const handleFinish = async (values) => {
    try {
      setLoading(true);
      const fileList = values?.attachment || [];
      const attachedDocuments = [];

      for (const file of fileList) {
        if (file?.originFileObj) {
          const uploaded = await handleDocumentSubmit(file.originFileObj);
          if (!uploaded) return;
          attachedDocuments.push({
            objectKey: uploaded.objectKey,
            fileName: uploaded.fileName,
          });
        } else if (file?.objectKey) {
          attachedDocuments.push({
            objectKey: file.objectKey,
            fileName: file.name,
          });
        }
      }

      const payload = {
        userId: userInfo?.userid || "",
        startDate: values.startdate.format("YYYY-MM-DD"),
        endDate: values.enddate.format("YYYY-MM-DD"),
        noOfDays: Number(values.noofdays),
        reason: values.reason,
        officeSupervisorId: currentUserRecord?.OfficeSupervisorId || "",
        officesupervisor: currentUserRecord?.officesupervisor || "",
        officeSupervisorEmailId: currentUserRecord?.OfficeSupervisorEmailId || officeSupervisorRecord?.userid,
        attachedDocuments,
        statusId: data?.requestId ? data?.statusId ?? "0" : "0",
        country: userInfo?.managingoffice[0] || currentUserRecord?.country || userInfo?.managingoffice?.[0] || "",
        wfhId: data?.wfhId || "",
      };

      const url = data?.requestId ? `wfh/updatewfhrequest` : `wfh/insertwfhrequest`;
      const method = data?.requestId ? "patch" : "post";

      const { code, status, response } = await CALL_FEATCH_API(url, method, payload);
      if (status === STATUS_CODE.SUCCESS || status === 200) {
        await getData();
        setTimeout(() => {
          setPopupOpen(false);
          form.resetFields();
          onCancel();
          message.success(response?.message || "WFH Created Successfully");
        }, 1000);
      } else if (status === "BAD_REQUEST" || code === STATUS_CODE.RECORD_EXIST) {
        message.error(response?.message || "A WFH request already exists for these dates");
      } else {
        message.error("Something went wrong. Please try again.");
      }
    } catch (err) {
      console.error(err);
      message.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleStartDateChange = (date) => {
    calculateDays(date, form.getFieldValue("enddate"));
  };

  const calculateDays = (start, end) => {
    const s = (start ?? form.getFieldValue("startdate"))?.clone();
    const e = (end ?? form.getFieldValue("enddate"))?.clone();
    if (s && e && !s.isAfter(e)) {
      let count = 0;
      let current = s.startOf("day");
      const endDay = e.startOf("day");
      while (current.isBefore(endDay) || current.isSame(endDay, "day")) {
        if (current.day() !== 0 && current.day() !== 6 && !getHolidayName(current)) {
          count++;
        }
        current = current.add(1, "day");
      }
      form.setFieldsValue({ noofdays: count });
    } else {
      form.setFieldsValue({ noofdays: 0 });
    }
  };

  return (
    <>
      <ModalWrap
        title={data?.requestId ? "Edit WFH Request" : "Apply for Work From Home"}
        open={open}
        footer={null}
        centered
        width={580}
        onCancel={() => {
          form.resetFields();
          onCancel();
        }}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={initialValues}
          onFinish={handleFinish}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Start Date"
                name="startdate"
                rules={[{ required: true, message: "Start date is required" }]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD-MM-YYYY"
                  onChange={(date) => handleDateSelect(date, "startdate")}
                  disabledDate={(c) =>
                    (c && c < dayjs().startOf("day")) || isDisabledDate(c)
                  }
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="End Date"
                name="enddate"
                rules={[{ required: true, message: "End date is required" }]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD-MM-YYYY"
                  onChange={(date) => handleDateSelect(date, "enddate")}
                  disabledDate={(current) =>
                    (current && current < dayjs().startOf("day")) || isDisabledDate(current)
                  }
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="No. of Days" name="noofdays">
            <Input disabled />
          </Form.Item>

          <Form.Item
            label="Reason for WFH"
            name="reason"
            rules={[
              { required: true, message: "Reason is required" },
              { max: 250, message: "Maximum 250 characters allowed" },
            ]}
          >
            <TextArea rows={4} maxLength={250} showCount />
          </Form.Item>

          <Form.Item
            label="Attachment (Optional)"
            name="attachment"
            valuePropName="fileList"
            getValueFromEvent={(e) => e?.fileList}
          >
            <Upload
              accept="image/*,.pdf"
              beforeUpload={(file) => {
                const isImageOrPdf =
                  file.type === "application/pdf" ||
                  file.type.startsWith("image/");
                if (!isImageOrPdf) {
                  message.error("Only image and PDF files are allowed");
                  return Upload.LIST_IGNORE;
                }
                return false;
              }}
              maxCount={1}
              className="custom-upload"
              showUploadList={{ showRemoveIcon: true }}
            >
              {!form.getFieldValue("attachment")?.length && (
                <div className="upload-box">
                  <UploadOutlined />
                  <span style={{ marginLeft: 8 }}>Upload File</span>
                </div>
              )}
            </Upload>
          </Form.Item>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <ButtonOutlined
              onClick={() => {
                form.resetFields();
                onCancel();
              }}
            >
              Cancel
            </ButtonOutlined>

            <ButtonFilled type="primary" htmlType="submit" loading={loading}>
              {data?.requestId ? "Save Changes" : "Submit"}
            </ButtonFilled>
          </div>
        </Form>
      </ModalWrap>

      <SuccessDeclinePopup
        open={popupOpen}
        setOpen={setPopupOpen}
        type="success"
        message={data?.requestId ? "Saved" : "Submitted"}
      />
    </>
  );
};

export default ApplyWorkFromHome;